/**
 * @module NotificationBell
 * @package @geenius/notifications-react-css
 * @description Renders the bell trigger used to open the notification panel in
 * the React vanilla CSS variant.
 */

import type { JSX } from 'react'
import { cx } from '../lib/cx'
import { useNotificationCopy } from '../lib/notificationCopy'

/**
 * Props for the React CSS notification bell trigger.
 */
export interface NotificationBellProps {
  unreadCount?: number
  onClick?: () => void
  isOpen?: boolean
  ariaControls?: string
  className?: string
}

/**
 * Renders the bell button used to toggle the notification panel.
 *
 * @param props Bell props including unread count and optional open state.
 * @returns The bell trigger element.
 */
export function NotificationBell({
  unreadCount = 0,
  onClick,
  isOpen = false,
  ariaControls,
  className,
}: NotificationBellProps): JSX.Element {
  const { plural, t } = useNotificationCopy()
  const accessibleCount =
    unreadCount > 99
      ? t('notifications.bell.countOverflowAria')
      : plural('notifications.bell.countAria', unreadCount)

  return (
    <button
      type="button"
      className={cx('notifications__bell', className)}
      onClick={onClick}
      aria-label={unreadCount > 0 ? accessibleCount : t('notifications.bell.openAria')}
      aria-haspopup="dialog"
      aria-expanded={isOpen}
      aria-controls={ariaControls}
    >
      <svg
        aria-hidden="true"
        className={cx('notifications__bell-icon')}
        focusable="false"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </svg>
      {unreadCount > 0 ? (
        <span className={cx('notifications__badge')} aria-hidden="true">
          {unreadCount > 99 ? '99+' : unreadCount}
        </span>
      ) : null}
    </button>
  )
}
